/**
 * Logique pure du filtre par tags de la vue Tâches.
 * Isolée du composant pour être testable (le harness vitest est nu,
 * sans jsdom : seules les fonctions pures sont couvrables).
 */

/** Sentinelle représentant « les tâches sans aucun tag ». */
export const NO_TAG = '__none__';

/**
 * Renvoie l'ensemble des identifiants de tâches à afficher.
 * Logique OU : une tâche passe si elle porte AU MOINS UN des tags filtrés.
 * `NO_TAG` se comporte comme un tag virtuel dans cette même logique.
 * Un filtre vide laisse tout passer.
 */
export function filterTaskIdsByTags(
  taskIds: string[],
  tagMap: Record<string, { id: string }[]>,
  tagFilterIds: string[],
): Set<string> {
  if (tagFilterIds.length === 0) return new Set(taskIds);

  const wanted = new Set(tagFilterIds);
  const includeUntagged = wanted.has(NO_TAG);

  return new Set(
    taskIds.filter((taskId) => {
      const tags = tagMap[taskId] ?? [];
      if (tags.length === 0) return includeUntagged;
      return tags.some((tag) => wanted.has(tag.id));
    }),
  );
}

/**
 * Retire du filtre persisté les identifiants ne correspondant plus à un tag
 * existant. Sans cela, un tag supprimé depuis les Paramètres viderait la vue
 * Tâches sans aucune indication à l'écran.
 */
export function pruneTagFilter(
  tagFilterIds: string[],
  existingTagIds: string[],
): string[] {
  const existing = new Set(existingTagIds);
  return tagFilterIds.filter((id) => id === NO_TAG || existing.has(id));
}

/** Libellé du bouton du filtre. */
export function tagFilterLabel(
  tagFilterIds: string[],
  tagsById: Map<string, { name: string }>,
): string {
  if (tagFilterIds.length === 0) return 'Toutes les étiquettes';
  if (tagFilterIds.length === 1) {
    const only = tagFilterIds[0] as string;
    if (only === NO_TAG) return 'Sans étiquette';
    const name = tagsById.get(only)?.name;
    return name ?? '1 étiquette';
  }
  return `${tagFilterIds.length} étiquettes`;
}
